import React from 'react';
import Icon from '../../../components/AppIcon';

const EscalationManagementPanel = ({ tickets, onRefresh }) => {
  const escalatedTickets = tickets?.filter(t => t?.slaStatus === 'breached' || t?.slaStatus === 'approaching_breach');

  const getSLABadge = (slaStatus) => {
    const slaStyles = {
      within_sla: { bg: 'bg-green-500/10', text: 'text-green-600', label: 'Within SLA' },
      approaching_breach: { bg: 'bg-amber-500/10', text: 'text-amber-600', label: 'Approaching Breach' },
      breached: { bg: 'bg-red-500/10', text: 'text-red-600', label: 'Breached' }
    };
    return slaStyles?.[slaStatus] || slaStyles?.within_sla;
  };

  const getPriorityBadge = (priority) => {
    const priorityStyles = {
      critical: { bg: 'bg-red-500/10', text: 'text-red-600', label: 'Critical' },
      high: { bg: 'bg-orange-500/10', text: 'text-orange-600', label: 'High' },
      medium: { bg: 'bg-yellow-500/10', text: 'text-yellow-600', label: 'Medium' },
      low: { bg: 'bg-green-500/10', text: 'text-green-600', label: 'Low' }
    };
    return priorityStyles?.[priority] || priorityStyles?.medium;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-foreground mb-2">SLA Escalation Monitor</h3>
        <p className="text-sm text-muted-foreground">
          Tickets requiring immediate attention due to SLA breach or approaching deadline
        </p>
      </div>

      {escalatedTickets?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="CheckCircle" size={48} className="text-green-500 mx-auto mb-3" />
          <p className="text-foreground font-medium">All tickets within SLA</p>
          <p className="text-sm text-muted-foreground mt-1">No escalations required at this time</p>
        </div>
      ) : (
        <div className="space-y-4">
          {escalatedTickets?.map(ticket => {
            const slaBadge = getSLABadge(ticket?.slaStatus);
            const priorityBadge = getPriorityBadge(ticket?.priority);

            return (
              <div
                key={ticket?.id}
                className="bg-card border-2 border-red-500/20 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-semibold text-foreground">{ticket?.ticketNumber}</h4>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${priorityBadge?.bg} ${priorityBadge?.text}`}>
                        {priorityBadge?.label}
                      </span>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${slaBadge?.bg} ${slaBadge?.text}`}>
                        {slaBadge?.label}
                      </span>
                    </div>
                    <p className="text-foreground font-medium mb-1">{ticket?.subject}</p>
                    <p className="text-sm text-muted-foreground">
                      {ticket?.companyName} • {ticket?.clientContactName}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-border">
                  <div>
                    <p className="text-xs text-muted-foreground">Assigned Agent</p>
                    <p className="text-sm font-medium text-foreground mt-1">
                      {ticket?.assignedAgent || 'Unassigned'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Created</p>
                    <p className="text-sm font-medium text-foreground mt-1">
                      {formatDateTime(ticket?.createdAt)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Resolution Due</p>
                    <p className="text-sm font-medium text-red-600 mt-1">
                      {formatDateTime(ticket?.resolutionDueAt)}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EscalationManagementPanel;